import { useEffect, useState, useCallback, useMemo, createContext, useContext } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Search, Sparkles, Loader2, Command as CommandIcon } from 'lucide-react'

import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command'
import { api } from '@/lib/api'
import { getAllCommands, getCommandById, type CommandEntry } from '@/lib/command-registry'
import { useRecentCommands } from '@/hooks/use-recent-commands'
import { useCommandSearch } from '@/hooks/use-command-search'
import { CreatorDialog } from '@/components/creators/creator-form-dialog'
import { CampaignDialog } from '@/components/backoffice/campaigns/campaign-dialog'

// ─── Fuzzy scoring ──────────────────────────────────────────────────────────
// We disable cmdk's built-in filter (shouldFilter={false}) and score commands
// ourselves. This lets us: (1) normalize accents, (2) do fuzzy subsequence
// matching, and (3) keep API creator results always at the top — unaffected
// by cmdk's global ranking.

/** Strip diacritics so "metrica" matches "métricas" */
function normalize(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/** Score a command against a search query. Higher = better. 0 = no match. */
function scoreCommand(cmd: CommandEntry, query: string): number {
  const q = normalize(query)
  if (!q) return 1

  const parts = [cmd.label, cmd.description ?? '', ...(cmd.keywords ?? [])]
  const text = normalize(parts.join(' '))

  // Exact substring — best matches
  if (text.includes(q)) {
    const words = text.split(/\s+/)
    if (words.some((w) => w.startsWith(q))) return 3 // prefix
    return 2 // substring
  }

  // Multi-word: every query word must be a substring
  const queryWords = q.split(/\s+/).filter(Boolean)
  if (queryWords.length > 1 && queryWords.every((w) => text.includes(w))) return 1.5

  // Subsequence (fuzzy) — only for 3+ char queries to avoid noise
  if (q.length >= 3) {
    let qi = 0
    for (let i = 0; i < text.length && qi < q.length; i++) {
      if (text[i] === q[qi]) qi++
    }
    if (qi === q.length) return 0.5
  }

  return 0
}

// ─── Context ─────────────────────────────────────────────────────────────────

type CommandPaletteContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null)

function useCommandPaletteContext() {
  const ctx = useContext(CommandPaletteContext)
  if (!ctx) throw new Error('useCommandPaletteContext must be used within CommandPaletteProvider')
  return ctx
}

// ─── Platform detection ─────────────────────────────────────────────────────

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)

// ─── Trigger Button ──────────────────────────────────────────────────────────

export function CommandPaletteTrigger() {
  const { setOpen } = useCommandPaletteContext()

  return (
    <button
      onClick={() => setOpen(true)}
      className="hidden md:flex items-center gap-2 h-8 w-56 rounded-md border border-border/60 bg-card px-2.5 text-[13px] text-muted-foreground/60 shadow-[inset_0_1px_2px_rgba(0,0,0,0.06),inset_0_0_0_1px_rgba(0,0,0,0.02)] hover:border-[rgba(16,185,129,0.3)] hover:text-muted-foreground transition-all duration-150 cursor-pointer"
      aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
    >
      <Search className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 text-left truncate">Buscar...</span>
      <span className="pointer-events-none inline-flex items-center gap-1">
        <kbd className="rounded border border-border/60 bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {isMac ? <CommandIcon className="h-3 w-3" /> : 'Ctrl'}
        </kbd>
        <span className="text-[10px] text-muted-foreground/50">+</span>
        <kbd className="rounded border border-border/60 bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          K
        </kbd>
      </span>
    </button>
  )
}

// ─── Sidebar Trigger (mobile — same input-like style, full width) ────────────

export function CommandPaletteSidebarTrigger({ onBeforeOpen }: { onBeforeOpen?: () => void } = {}) {
  const { setOpen } = useCommandPaletteContext()

  return (
    <button
      onClick={() => {
        onBeforeOpen?.()
        // Small delay so sidebar sheet closes before dialog opens
        setTimeout(() => setOpen(true), 150)
      }}
      className="flex items-center gap-2 h-8 w-full rounded-md border border-border/60 bg-card px-2.5 text-[13px] text-muted-foreground/60 shadow-[inset_0_1px_2px_rgba(0,0,0,0.06),inset_0_0_0_1px_rgba(0,0,0,0.02)] hover:border-[rgba(16,185,129,0.3)] hover:text-muted-foreground transition-all duration-150 cursor-pointer"
    >
      <Search className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 text-left truncate">Buscar...</span>
    </button>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function CommandPalette({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [activeDialog, setActiveDialog] = useState<string | null>(null)

  const navigate = useNavigate()
  const { recentIds, addRecent } = useRecentCommands()

  // Super-admin check (same pattern as app-sidebar.tsx)
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get(),
  })
  const isSuperAdmin = settings?.slug === 'rafobase-admin'

  const commands = getAllCommands(isSuperAdmin)
  const { creators, isLoading: isSearchingCreators } = useCommandSearch(search)

  // ─── Manual fuzzy filtering ────────────────────────────────────────────

  const filteredGrouped = useMemo(() => {
    const q = search.trim()

    const scored = q
      ? commands
          .map((cmd) => ({ cmd, score: scoreCommand(cmd, q) }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
      : commands.map((cmd) => ({ cmd, score: 1 }))

    const grouped: Record<string, CommandEntry[]> = {}
    for (const { cmd } of scored) {
      if (!grouped[cmd.group]) grouped[cmd.group] = []
      grouped[cmd.group].push(cmd)
    }
    return grouped
  }, [commands, search])

  const hasFilteredCommands = Object.values(filteredGrouped).some((g) => g.length > 0)

  // ─── Keyboard shortcut ──────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setSearch('')
  }, [])

  const handleSelect = useCallback(
    (command: CommandEntry) => {
      addRecent(command.id)

      if (command.action) {
        setOpen(false)
        // Small delay so the palette closes smoothly before dialog opens
        setTimeout(() => setActiveDialog(command.action!), 150)
        return
      }

      if (command.href) {
        setOpen(false)
        navigate({ to: command.href })
      }
    },
    [addRecent, navigate]
  )

  const handleCreatorSelect = useCallback(
    (creatorId: string) => {
      setOpen(false)
      navigate({ to: '/creators/$id', params: { id: creatorId }, search: { tab: 'overview' } })
    },
    [navigate]
  )

  // ─── Recent commands ──────────────────────────────────────────────────

  const recentCommands = recentIds
    .map((id) => getCommandById(id, isSuperAdmin))
    .filter((cmd): cmd is CommandEntry => cmd != null)

  // Group order
  const groupOrder = ['Ações', 'Navegação', 'Configurações', 'Integrações', 'Backoffice']

  // ─── Empty state check ────────────────────────────────────────────────
  const showEmpty =
    search.trim().length > 0 &&
    !hasFilteredCommands &&
    !isSearchingCreators &&
    creators.length === 0

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen }}>
      {children}

      <CommandDialog open={open} onOpenChange={handleOpenChange} shouldFilter={false}>
        <CommandInput placeholder="Buscar ou navegar..." value={search} onValueChange={setSearch} />
        <CommandList>
          {/* Empty state (manual — cmdk's CommandEmpty doesn't work with shouldFilter={false}) */}
          {showEmpty && (
            <div className="py-8 text-center text-[13px] text-muted-foreground">
              Nenhum resultado encontrado.
            </div>
          )}

          {/* Recent commands (only when search is empty) */}
          {!search && recentCommands.length > 0 && (
            <CommandGroup heading="Recentes">
              {recentCommands.map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  value={`recent-${cmd.id}`}
                  onSelect={() => handleSelect(cmd)}
                >
                  <cmd.icon className="text-muted-foreground" />
                  <span>{cmd.label}</span>
                  {cmd.description && (
                    <span className="ml-auto text-[11px] text-muted-foreground/50 truncate max-w-[200px]">
                      {cmd.description}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {!search && recentCommands.length > 0 && <CommandSeparator />}

          {/* Creator search results (API) — always at top when searching */}
          {search.length >= 2 && (isSearchingCreators || creators.length > 0) && (
            <CommandGroup heading="Creators">
              {isSearchingCreators && creators.length === 0 && (
                <div className="flex items-center justify-center py-4 text-[12px] text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                  Buscando creators...
                </div>
              )}
              {creators.map((creator) => (
                <CommandItem
                  key={creator.id}
                  value={`creator-${creator.id}`}
                  onSelect={() => handleCreatorSelect(creator.id)}
                >
                  <Sparkles className="text-muted-foreground" />
                  <div className="flex flex-col">
                    <span>{creator.name}</span>
                    {creator.instagramUsername && (
                      <span className="text-[11px] text-muted-foreground/50">
                        @{creator.instagramUsername}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Static command groups — manually filtered & sorted by score */}
          {groupOrder.map((groupName) => {
            const groupCommands = filteredGrouped[groupName]
            if (!groupCommands?.length) return null

            return (
              <CommandGroup key={groupName} heading={groupName}>
                {groupCommands.map((cmd) => (
                  <CommandItem key={cmd.id} value={cmd.id} onSelect={() => handleSelect(cmd)}>
                    <cmd.icon className="text-muted-foreground" />
                    <span>{cmd.label}</span>
                    {cmd.description && (
                      <span className="ml-auto text-[11px] text-muted-foreground/50 truncate max-w-[200px]">
                        {cmd.description}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )
          })}
        </CommandList>

        {/* Footer with keyboard hints */}
        <div className="flex items-center gap-3 border-t border-border/60 px-3 py-2">
          <span className="text-[11px] text-muted-foreground/50 flex items-center gap-1">
            <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px]">↑↓</kbd>
            Navegar
          </span>
          <span className="text-[11px] text-muted-foreground/50 flex items-center gap-1">
            <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px]">↵</kbd>
            Selecionar
          </span>
          <span className="text-[11px] text-muted-foreground/50 flex items-center gap-1">
            <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px]">Esc</kbd>
            Fechar
          </span>
          <span className="text-[11px] text-muted-foreground/50 flex items-center gap-1 ml-auto">
            <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px]">
              {isMac ? '⌘' : 'Ctrl'}
            </kbd>
            <kbd className="rounded border bg-muted px-1 py-0.5 text-[10px]">K</kbd>
            Abrir
          </span>
        </div>
      </CommandDialog>

      {/* Action dialogs */}
      <CreatorDialog
        open={activeDialog === 'create-creator'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      />
      <CampaignDialog
        open={activeDialog === 'create-campaign'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      />
    </CommandPaletteContext.Provider>
  )
}
